import { apiAuth, apiResponseType } from ".";

/**
 * Ferramenta administrativa de normalização de vendas inconsistentes.
 *
 * Trata vendas que ficaram em um estado impossível — tipicamente pré-vendas
 * aprovadas e recusadas quase ao mesmo tempo, que herdaram etapas de
 * cancelamento e por isso não avançam, não podem ser canceladas e nunca
 * chegam a "Aprovada" (portanto também ficam fora da comissão do vendedor).
 */

export type SaleNormalizationDirection = "keep_approval" | "keep_rejection";

export interface SaleNormalizationIssue {
  code: string;
  label: string;
  detail: string;
  data: unknown;
}

export interface SaleNormalizationAction {
  action:
    | "delete_step"
    | "update_step"
    | "create_step"
    | "move_chat"
    | "update_receivable"
    | "clear_pre_sale_rejection"
    | "mark_pre_sale_rejected"
    | "update_sale_status"
    | "delegate_cancel"
    | "manual_review"
    | "note";
  description: string;
  table?: string;
  id?: number;
  field?: string;
  from?: number;
  to?: number;
  sale_step_id?: number;
  process_status_id?: number;
}

export interface SaleNormalizationSale {
  id: number;
  uuid: string;
  total: string | number;
  sale_status_id: number;
  sale_status: string;
  sale_type: string | null;
  unity: string | null;
  client: string | null;
  seller: string | null;
  seller_id: number | null;
  created_at: string;
}

export interface SaleNormalizationStep {
  id: number;
  sale_step_id: number;
  sale_step: string;
  process_status_id: number;
  process_status: string | null;
  created_at: string;
  updated_at: string;
}

export interface SaleNormalizationDiagnosis {
  sale: SaleNormalizationSale;
  issues: SaleNormalizationIssue[];
  is_inconsistent: boolean;
  steps: SaleNormalizationStep[];
  directions: Record<SaleNormalizationDirection, SaleNormalizationAction[]>;
}

export interface SaleNormalizationPreview {
  sale: SaleNormalizationSale;
  issues: SaleNormalizationIssue[];
  direction: SaleNormalizationDirection;
  finalize_delivery: boolean;
  plan: SaleNormalizationAction[];
  preview_hash: string;
}

export interface SaleNormalizationApplyPayload {
  direction: SaleNormalizationDirection;
  preview_hash: string;
  finalize_delivery: boolean;
  confirmed: true;
  reason: string;
}

export interface SaleNormalizationFilters {
  sale_id?: number | string;
  unity_id?: number | string;
  seller_id?: number | string;
  start_date?: string;
  end_date?: string;
  per_page?: number;
}

const buildQuery = (page: number, filters: SaleNormalizationFilters = {}) => {
  const params = new URLSearchParams();

  params.append("page", String(page));

  Object.entries(filters).forEach(([key, value]) => {
    if (value === undefined || value === null || value === "") return;
    params.append(key, String(value));
  });

  return params.toString();
};

export const getInconsistentSales = (
  page = 1,
  filters: SaleNormalizationFilters = {}
): apiResponseType => apiAuth.get(`sale-normalization?${buildQuery(page, filters)}`);

export const getSaleNormalizationDiagnosis = (
  saleId: number,
  finalizeDelivery = false
): apiResponseType =>
  apiAuth.get(`sale-normalization/${saleId}?finalize_delivery=${finalizeDelivery ? 1 : 0}`);

export const previewSaleNormalization = (
  saleId: number,
  direction: SaleNormalizationDirection,
  finalizeDelivery = false
): apiResponseType =>
  apiAuth.post(`sale-normalization/${saleId}/preview`, {
    direction,
    finalize_delivery: finalizeDelivery,
  });

export const applySaleNormalization = (
  saleId: number,
  data: SaleNormalizationApplyPayload
): apiResponseType => apiAuth.post(`sale-normalization/${saleId}/apply`, data);

export const getSaleNormalizationAudits = (
  page = 1,
  filters: { sale_id?: number | string; direction?: string; per_page?: number } = {}
): apiResponseType => apiAuth.get(`sale-normalization/audits?${buildQuery(page, filters)}`);
